import { useMonyawn } from "../context/MonyawnContext";
import {
  createCheckpoint,
  createTask,
  createStageEvent,
  createMemo,
  createDM,
  nowIso,
  isClosedStage,
  stageMeta,
} from "../workflow";
import { WorkflowMachine } from "../workflow/machine";
import { OpportunityStatus } from "../types";

export function useOpportunityAdvance() {
  const { state, patchState, selectedOpportunity, setNotice } = useMonyawn();

  const handleAdvanceStage = () => {
    if (!selectedOpportunity) return;

    const transition = WorkflowMachine.getNextTransition(selectedOpportunity);
    if (!transition) {
      setNotice({ tone: "info", message: "This opportunity is already at a terminal stage." });
      return;
    }

    const guard = WorkflowMachine.canMove(selectedOpportunity, state);
    if (!guard.passed) {
      setNotice({ tone: "info", message: `Move blocked: ${guard.failures[0]}` });
      return;
    }

    const nextStage = transition.to;
    const nextStatus: OpportunityStatus =
      nextStage === "closed_won"
        ? "closed_won"
        : nextStage === "closed_lost"
          ? "closed_lost"
          : "active";

    const updatedOpportunity = {
      ...selectedOpportunity,
      current_stage: nextStage,
      updated_at: nowIso(),
      status: nextStatus,
    };

    const checkpoint = createCheckpoint(
      updatedOpportunity,
      stageMeta[nextStage].label,
      `Pre-stage advance check for ${stageMeta[nextStage].label}`,
      "high",
      nextStage === "outreach_ready" ||
        nextStage === "offer_review" ||
        nextStage === "intake_complete" ||
        nextStage === "positioning"
        ? "escalate_for_review"
        : "proceed",
      `AI evaluation before advancing to ${stageMeta[nextStage].label}.`,
      "medium",
      "low",
    );

    const isBlocking =
      checkpoint.decision === "block" || checkpoint.decision === "escalate_for_review";
    const requiresHumanReview = checkpoint.human_review_required;

    if (isBlocking) {
      setNotice({
        tone: "info",
        message: `Stage advance blocked: ${checkpoint.evidence_summary}. Decision: ${checkpoint.decision}.`,
      });
      return;
    }

    if (requiresHumanReview) {
      setNotice({
        tone: "info",
        message: `Human review required before proceeding to ${stageMeta[nextStage].label}. Assigned to: ${checkpoint.assigned_reviewer_role || "N/A"}.`,
      });
    }

    const task = createTask(
      selectedOpportunity.opportunity_id,
      `Complete ${stageMeta[nextStage].label.toLowerCase()} review`,
      stageMeta[nextStage].reviewerRole,
      !isClosedStage(nextStage),
    );

    const stageEvent = createStageEvent(
      selectedOpportunity.opportunity_id,
      nextStage,
      "advanced",
      "user",
      state.selectedUserId!,
      `Advanced from ${stageMeta[selectedOpportunity.current_stage].label} to ${stageMeta[nextStage].label}.`,
    );

    const memo = createMemo(
      selectedOpportunity.opportunity_id,
      nextStage === "offer_review"
        ? "offer"
        : nextStage === "fit_review"
          ? "fit"
          : nextStage === "positioning"
            ? "positioning"
            : nextStage === "interview_active"
              ? "interview"
              : "final",
      stageMeta[nextStage].description,
      checkpoint.confidence_level,
      false,
    );

    const proactiveDraft =
      nextStage === "interview_active"
        ? createDM(
            selectedOpportunity.opportunity_id,
            "email",
            `Follow up: ${selectedOpportunity.role_title} at ${selectedOpportunity.company_name}`,
            `Hi team,\n\n[AI Wingman is drafting this follow-up based on your debrief notes...]\n\nBest,\n`,
          )
        : null;

    patchState(
      (current) => ({
        ...current,
        opportunities: current.opportunities.map((opportunity) =>
          opportunity.opportunity_id === updatedOpportunity.opportunity_id
            ? updatedOpportunity
            : opportunity,
        ),
        checkpoints: [checkpoint, ...current.checkpoints],
        tasks: [task, ...current.tasks],
        memos: [memo, ...current.memos],
        events: [stageEvent, ...current.events],
        dms: proactiveDraft ? [proactiveDraft, ...current.dms] : current.dms,
      }),
      `Opportunity advanced into ${stageMeta[nextStage].label}.`,
    );
  };

  return { handleAdvanceStage };
}
